import type { PaginationType } from '../types/Pagination.types';
import type { Market } from '@/libs/market/market';
import { z } from 'zod';
import { MARKET_SOURCES, MARKETS } from '@/libs/market/market';
import { MediaDtoSchema } from '../media/media.schema';
import { VerificationStateSchema } from '../verifications/verifications.schema';

export const UserDtoSchema = z.object({
  id: z.string().uuid(), // Unique identifier of the user
  email: z.string().email(), // Email address of the user
  firstName: z.string().optional().nullable(), // First name of the user
  lastName: z.string().optional().nullable(), // Last name of the user
  is2faEnabled: z.boolean(), // Whether the user has enabled 2FA
  isEmailVerified: z.boolean(), // Whether the user is email verified
  isPolicyAccepted: z.boolean(),
  isTest: z.boolean(), // Whether the user is used for testing purposes
  isWithdrawAllowed: z.boolean(),
  isDepositAllowed: z.boolean(),
  isFraudulent: z.boolean(),
  isVpnDetected: z.boolean().optional(),
  isAllowedSendAdwEmail: z.boolean(), // Whether the user has allowed to receive marketing emails
  isAppInstalled: z.boolean().optional(), // Whether the user has installed the PWA
  isVip: z.boolean(),
  languageCode: z.string(), // Language code of the user
  // Optional during the backend rollout. A missing source is treated as
  // unresolved so a legacy response cannot trigger a market redirect.
  market: z.enum(MARKETS).nullish(),
  marketSource: z.enum(MARKET_SOURCES).optional(),
  externalUserId: z.string().nullable(),
  country: z.string().nullable(),
  region: z.string().nullable(),
  phone: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  verificationState: VerificationStateSchema,
  avatar: MediaDtoSchema.nullable(), // Avatar of the user
  deactivatedBy: z.string().uuid().nullable(),
  deactivatedAt: z.string().datetime().nullable(),
  clientFacingDeactivateReason: z.string().nullable(),
  internalDeactivateReason: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  isKycVerified: z.boolean(),
  address: z.object({
    addressLine1: z.string(),
    addressLine2: z.string().optional().nullable(),
    city: z.string(),
    region: z.string().optional().nullable(),
    postalCode: z.string(),
    countryCode: z.string().optional().nullable(),
  }).nullable(),
}).describe('UserDtoSchema');

export const createUpdateUserAddressSchema = ({
  postalCodeRequired = 'ZIP code is required',
}: { postalCodeRequired?: string } = {}) => z.object({
  addressLine1: z.string().min(1, 'Street address is required'),
  addressLine2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  postalCode: z.string().min(1, postalCodeRequired),
});

export const UpdateUserAddressSchema = createUpdateUserAddressSchema();

export const UpdateUserDtoSchema = z.object({
  firstName: z.string().min(1, 'First name is required').optional(), // First name of the user
  avatarId: z.string().uuid().optional(), // Avatar of the user
  address: UpdateUserAddressSchema.optional(),
});

export const SecurityFormValuesSchema = z.object({
  currentPassword: z.string().min(8, 'Password must contain at least 8 characters'),
  newPassword: z.string().min(8, 'Password must contain at least 8 characters'),
  confirmNewPassword: z.string().min(8, 'Password must contain at least 8 characters'),
}).refine(data => data.newPassword === data.confirmNewPassword, {
  message: 'Your password doesn’t match',
  path: ['confirmNewPassword'],
});

export const UserPaymentMethodStateSchema = z.enum([
  'not-submitted',
  'pending',
  'approved',
  'rejected',
  'on-hold',
  'resubmission-requested',
]);

export const UserPaymentMethodKey = {
  CARD: 'prepaid-card',
  CRYPTO: 'crypto',
  FAVORITE_CARD: 'card',
  PAYPAL: 'paypal',
  CASHAPP: 'cashapp',
  INTERAC: 'interac',
  ECASHOUT: 'e-cashout',
} as const;

export const UserPaymentMethodKeySchema = z.nativeEnum(UserPaymentMethodKey);

export const UserPaymentMethodCurrencyTypeSchema = z.enum(['fiat', 'crypto']);

export const UserPaymentMethodDtoSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  isActive: z.boolean(),
  // Mirrors `activationLockedAt`; absent means not locked.
  isActivationLocked: z.boolean().optional(),
  type: UserPaymentMethodKeySchema,
  currencyType: UserPaymentMethodCurrencyTypeSchema,
  lastUsedAt: z.string().datetime().optional().nullable(),
  totalDepositAmount: z.number(),
  state: UserPaymentMethodStateSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  receiver: z.string(),
  note: z.string().optional().nullable(),
  brand: z.string().nullable(),
  expiryYear: z.string().nullable(),
  expiryMonth: z.string().nullable(),
}).describe('UserPaymentMethodDtoSchema');

export const VerifyPaymentMethodResponseSchema = z.object({
  token: z.string(),
});

export const PushSubscriptionTypeSchema = z.enum([
  'chrome-push',
  'safari-push',
  'firefox-push',
  'windows-push',
  'mac-os-push',
  'i-os-push',
  'android-push',
  'huawei-push',
  'fire-os-push',
  'email',
  'sms',
]);

export const SubscribePushNotificationsDtoSchema = z.object({
  token: z.string(),
  type: PushSubscriptionTypeSchema,
});

export const PaymentMethodVerificationStateSchema = UserPaymentMethodStateSchema;

export const PaymentMethodVerificationStatusUpdateMessageSchema = z.object({
  userId: z.string().uuid(),
  paymentMethodId: z.string().uuid(),
  kycName: z.string().nullable(),
  paymentMethodDisplay: z.string(),
  timestamp: z.string().datetime(),
  state: PaymentMethodVerificationStateSchema,
});

export const UserMarketAssignedMessageSchema = z.object({
  userId: z.string().uuid(),
  market: z.enum(MARKETS),
  marketSource: z.enum(MARKET_SOURCES),
  timestamp: z.string().datetime(),
});

export const VpnDetectedMessageSchema = z.object({
  isVpnDetected: z.boolean(),
});

export const AllowedSendMarketingEmailsRequestSchema = z.object({
  isAllowed: z.boolean(),
});

export type UserPaymentMethodDto = z.infer<typeof UserPaymentMethodDtoSchema>;
export type UserDto = z.infer<typeof UserDtoSchema>;
export type UpdateUserDto = z.infer<typeof UpdateUserDtoSchema>;
export type UpdateUserAddress = z.infer<typeof UpdateUserAddressSchema>;
export type SecurityFormValues = z.infer<typeof SecurityFormValuesSchema>;
export type UserPaymentMethodsDto = PaginationType<UserPaymentMethodDto>;
export type UserPaymentMethodState = z.infer<typeof UserPaymentMethodStateSchema>;
export type UserPaymentMethodKeyType = (typeof UserPaymentMethodKey)[keyof typeof UserPaymentMethodKey];
export type UserPaymentMethodCurrencyType = z.infer<typeof UserPaymentMethodCurrencyTypeSchema>;
export type VerifyPaymentMethodResponse = z.infer<typeof VerifyPaymentMethodResponseSchema>;
export type PaymentMethodVerificationState = z.infer<typeof PaymentMethodVerificationStateSchema>;
export type PaymentMethodVerificationStatusUpdateMessage = z.infer<typeof PaymentMethodVerificationStatusUpdateMessageSchema>;
export type UserMarketAssignedMessage = z.infer<typeof UserMarketAssignedMessageSchema>;
export type VpnDetectedMessage = z.infer<typeof VpnDetectedMessageSchema>;
export type PushSubscriptionType = z.infer<typeof PushSubscriptionTypeSchema>;
export type SubscribePushNotificationsDto = z.infer<typeof SubscribePushNotificationsDtoSchema>;
export type AllowedSendMarketingEmailsRequest = z.infer<typeof AllowedSendMarketingEmailsRequestSchema>;
export type UserMarketType = Market;
